/* eslint-disable jsx-a11y/click-events-have-key-events */
import clsx from 'clsx';
import _ from 'lodash';
import memoize from 'memoize-one';
import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import Button from '@/src/components/common/Elements/Button/Button';
import Icon, { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import { Loading } from '@/src/globalComponents';
import { useAsync } from '@/src/hooks/useAsync';
import { fetchLibraryDocuments as reducerfetchLibraryDocuments } from '@/src/reducers/project/files/files.service';

import DocumentListItem from './DocumentListItem';

const TYPE_FOLDER = 'folder';
const TYPE_DOCUMENT = 'document';
const INITIAL_STATE = {
  documentList: [],
  selectedFolders: [],
  selectedDocuments: [],
};

const DocumentList = ({ setSourceDocumentList }) => {
  const projectId = useSelector((state) => state.project.Selected.id);
  const selectAllRef = useRef();
  const firstRowRef = useRef();
  const [{ documentList, selectedFolders }, setState] = useState(INITIAL_STATE);

  const { status, execute: fetchLibraryDocuments } = useAsync(reducerfetchLibraryDocuments, {
    immediate: false,
  });

  const isLoading = status === 'loading';

  // Execute on mount only
  useEffect(() => {
    fetchRootDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Executes whenever documentList changes
  useEffect(() => {
    setSourceDocumentList(checkedDocumentList(documentList));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentList]);

  const fetchRootDocuments = () => {
    fetchDocuments({ type: TYPE_FOLDER });
    setState((prevState) => ({
      ...prevState,
      selectedFolders: [],
    }));
  };

  const fetchFolderDocuments = ({ document, index }) => {
    setState((prevState) => ({
      ...prevState,
      selectedFolders: selectedFolders.slice(0, index + 1),
    }));
    fetchDocuments({ folderId: document.id });
  };

  const fetchDocuments = async ({ type, folderId }) => {
    const response = await fetchLibraryDocuments({
      projectId,
      type,
      folderId,
    });

    if (response) {
      setState((prevState) => ({
        ...prevState,
        documentList: response.data.results,
      }));
    }
  };

  const fetchSubDocuments = async (document) => {
    if (document.type === TYPE_DOCUMENT) {
      return;
    }

    firstRowRef.current.scrollIntoView({ behavior: 'instant' });

    setState((prevState) => ({
      ...prevState,
      selectedFolders: [...selectedFolders, document],
    }));

    fetchDocuments({ folderId: document.id });
  };

  const handleCheckAll = () => {
    const isChecked = selectAllRef.current.checked;

    setState((prevState) => ({
      ...prevState,
      documentList: documentList.map((document) => {
        if (document.type === TYPE_FOLDER) {
          return document;
        }

        return {
          ...document,
          isChecked: isChecked,
        };
      }),
    }));
  };

  const handleCheckDocument = (selectedDocument) => {
    const newDocumentList = [...documentList];
    const index = _.findIndex(newDocumentList, { id: selectedDocument.id });

    if (index >= 0) {
      const newDocument = {
        ...selectedDocument,
        isChecked: !selectedDocument.isChecked,
      };

      newDocumentList.splice(index, 1, { ...newDocumentList[index], ...newDocument });

      selectAllRef.current.checked = checkIsAllSelected(documentList);

      setState((prevState) => ({
        ...prevState,
        documentList: newDocumentList,
      }));
    }
  };

  const handleLoadMore = () => {};

  const documentListWithDocuments = memoize((documentList) =>
    documentList.filter((doc) => doc.type === TYPE_DOCUMENT)
  );

  const checkedDocumentList = memoize((documentList) =>
    documentList.filter((doc) => doc.isChecked)
  );

  const checkIsAllSelected = memoize(
    (documentList) =>
      checkedDocumentList(documentList).length === documentListWithDocuments(documentList).length
  );

  const hasDocuments = documentListWithDocuments(documentList).length > 0;
  const isAllSelected = checkIsAllSelected(documentList);
  const hasMore = false;
  const containerClasses = clsx('library-files');

  return (
    <React.Fragment>
      <div className={`files-container ${containerClasses}`}>
        <div
          className={clsx(
            'container--libraryBreadcrumb',
            'tw-flex tw-items-center',
            'tw-px-[10px]'
          )}
        >
          <span className={clsx('tw-flex tw-items-center')}>
            <Button
              className={clsx(
                'tw-mx-[5px] tw-p-[4px]',
                'tw-text-cfo-black tw-text-[16px] tw-font-brandonTextMedium',
                'hover:tw-underline'
              )}
              onClick={fetchRootDocuments}
            >
              Library
            </Button>
          </span>
          {selectedFolders.length > 0 &&
            selectedFolders.map((document, index) => {
              const fName =
                document.documentNameCount > 0
                  ? `${document.origin}(${document.documentNameCount})`
                  : document.origin;
              return (
                <span className={clsx('tw-group', 'tw-flex tw-items-center')} key={index}>
                  <Icon
                    className={clsx('tw-mx-[5px]', 'tw-text-cfo-black')}
                    icon={ICONS.arrow_right}
                  />
                  <Button
                    className={clsx(
                      'tw-mx-[5px] tw-p-[4px]',
                      'tw-text-cfo-black tw-text-[16px] tw-font-brandonTextMedium',
                      'hover:tw-underline',
                      'group-last:tw-pointer-events-none'
                    )}
                    onClick={() => fetchFolderDocuments({ document, index })}
                  >
                    {fName}
                  </Button>
                </span>
              );
            })}
        </div>
        <table className="table-document table-overflow mb10">
          <thead>
            <tr className="mr45 pdr0 bb">
              <th style={{ width: '362px' }} className="text-left text-black">
                <div className="columnItemContainer">
                  {hasDocuments && (
                    <label className="custom-checkbox float-left">
                      <input
                        type="checkbox"
                        className="clickable mr20"
                        onClick={handleCheckAll}
                        ref={selectAllRef}
                        checked={isAllSelected ? 1 : 0}
                        title={isAllSelected ? 'Unselect All' : 'Select All'}
                      />
                      <span className="checkmark"></span>
                    </label>
                  )}
                  <span>Name</span>
                </div>
              </th>
              <th style={{ width: '200px' }} className="text-center text-black">
                <div className="columnItemContainer">Uploaded by</div>
              </th>
              <th style={{ width: '200px' }} className="text-center text-black">
                <div className="columnItemContainer">Upload Date</div>
              </th>
            </tr>
          </thead>
          <tbody className={`${documentList.length === 0 ? 'hide' : ''}`}>
            {documentList.map((document, index) => (
              <DocumentListItem
                key={`${document.id}_${index}`}
                innerRef={index === 0 ? firstRowRef : null}
                document={document}
                onFetchSubDocuments={fetchSubDocuments}
                onCheckDocument={handleCheckDocument}
              />
            ))}
            {hasMore && (
              <tr>
                <td colSpan="8" className="load-more-row">
                  <p className="mb0 pd5 text-center">
                    <a
                      className="text-grey"
                      onClick={() => {
                        handleLoadMore();
                      }}
                      role="button"
                      tabIndex="0"
                    >
                      Load More
                    </a>
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {isLoading && (
          <div
            className={clsx(
              'container--loader',
              'tw-flex tw-items-center tw-justify-center',
              'tw-absolute tw-left-0 tw-top-0 tw-z-[5]',
              'tw-h-full tw-w-full',
              'tw-bg-[rgba(255,255,255,0.9)]'
            )}
          >
            <Loading />
          </div>
        )}

        {documentList.length === 0 && !isLoading && (
          <p
            className={clsx(
              'tw-font-brandonTextMedium tw-text-[14px] tw-text-cfo-black tw-text-left',
              'tw-m-[20px]'
            )}
          >
            No Records Found
          </p>
        )}
      </div>
    </React.Fragment>
  );
};

export default DocumentList;
